import { Calendar, Phone, Mail, FileText, CheckSquare, Plus, Pencil, Archive } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import {
  ACTIVITY_TYPE_LABEL,
  type ActivityType,
} from '../constants';
import {
  useActivities,
  useArchiveActivity,
  useCreateActivity,
  useUpdateActivity,
} from '../queries';
import type { Activity, ActivityInput } from '../types';
import { ActivityFormDialog } from './ActivityFormDialog';

/** Which entity this panel is scoped to. The matching FK is sent as a list-param,
 *  so the server returns only activities linked to this object. */
export type ActivitiesScope =
  | { entity: 'lead'; entityId: number }
  | { entity: 'company'; entityId: number }
  | { entity: 'contact'; entityId: number }
  | { entity: 'deal'; entityId: number };

interface ActivitiesPanelProps {
  scope: ActivitiesScope;
  /** Related CRM FKs to pre-fill on a new activity (e.g. a deal's company /
   *  contact / lead), so the user doesn't re-link what we already know. The
   *  scoped entity always wins over these. */
  relatedDefaults?: Partial<Record<'lead' | 'company' | 'contact' | 'deal', number | null>>;
}

const ICONS_BY_TYPE: Record<ActivityType, typeof Calendar> = {
  call: Phone,
  meeting: Calendar,
  email: Mail,
  note: FileText,
  task: CheckSquare,
};

/** Activity timeline scoped to one CRM entity. Mirrors CommentsPanel's pattern
 *  — read via React Query, write via the existing ActivityFormDialog. */
export function ActivitiesPanel({ scope, relatedDefaults }: ActivitiesPanelProps) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Activity | null>(null);
  const create = useCreateActivity();
  const update = useUpdateActivity();
  const archive = useArchiveActivity();

  // Map the scope to the BE filter param (one of company/contact/deal).
  const listParams = { [scope.entity]: scope.entityId } as const;
  const { data, isPending, isError, error } = useActivities(listParams);

  async function handleSubmit(input: ActivityInput) {
    if (editing) {
      await update.mutateAsync({ id: editing.id, input });
    } else {
      await create.mutateAsync(input);
    }
  }

  const activities = data?.results ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Activities</CardTitle>
          <CardDescription>
            Calls, meetings, emails, notes, and tasks linked to this {scope.entity}.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Log activity
        </Button>
      </CardHeader>
      <CardContent>
        {isError ? (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load activities.'}
          </p>
        ) : isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : activities.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            No activities logged yet.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {activities.map((a) => {
              const Icon = ICONS_BY_TYPE[a.type] ?? FileText;
              return (
                <li key={a.id} className="flex items-start gap-3 p-3 text-sm">
                  <Icon
                    className="text-muted-foreground mt-0.5 size-4 shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{a.subject}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {ACTIVITY_TYPE_LABEL[a.type]}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {new Date(a.occurred_at).toLocaleString()}
                    </p>
                    {a.notes ? (
                      <p className="mt-1 text-sm whitespace-pre-wrap">{a.notes}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Edit activity"
                      onClick={() => setEditing(a)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Archive activity"
                      onClick={() => archive.mutate(a.id)}
                      disabled={archive.isPending}
                    >
                      <Archive className="size-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* Create dialog — pre-fills the scoped FK so the new activity is linked. */}
      <ActivityFormDialog
        open={creating}
        onOpenChange={setCreating}
        defaults={{ ...relatedDefaults, [scope.entity]: scope.entityId }}
        onSubmit={handleSubmit}
      />

      {/* Edit dialog. */}
      {editing ? (
        <ActivityFormDialog
          open={Boolean(editing)}
          onOpenChange={(o) => !o && setEditing(null)}
          activity={editing}
          onSubmit={handleSubmit}
        />
      ) : null}
    </Card>
  );
}
